import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "@aws-amplify/ui-react/styles.css";

import {
  Box,
  Container,
  Divider,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";

import {
  CountryCode,
  CreateBaptismInput,
  State,
  Timezone,
  USAddress,
  ValidationError,
} from "baptize-america-client";
import { Dayjs } from "dayjs";

import { useAlert } from "../../components/AlertProvider";
import TimePicker from "../../components/TimePicker";
import { createBaptism, getChurch } from "../../utils/client";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";
import {
  isAdmin,
  isChurchManager,
  dateFromDateTimeTzStr,
} from "../../utils/helper";
import { useUserData } from "../../components/DataProvider";

const styles = {
  titleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mb: 5,
  },
  innerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 2,
    mb: 2,
    width: "100%",
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    mt: 2,
    mb: 2,
    flexGrow: 1,
  },
  saveButtonBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 5,
  },
  saveButton: {
    backgroundColor: "#00169E",
    color: "#FFF",
    justifyContent: "center",
    height: 50,
    width: 300,
    fontSize: 16,
  },
  infoBox: {
    bgcolor: "rgba(123, 123, 123, 0.2)",
    width: "100%",
    p: 1,
    borderRadius: 2,
  },
  infoIcon: {
    display: "flex",
    alignSelf: "center",
    mr: 1,
    color: "rgba(123, 123, 123, 1)",
  },
};

/**
 * TODO:
 ** add Baptism Events
 ** validate/compare fetched vs. updated church details before calling updateChurch
 **/
const CreateBaptismEvent = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { churchId } = useParams();
  const { attributes } = useUserData();
  const { setAlert } = useAlert();
  const navigate = useNavigate();

  const [location, setLocation] = useState<USAddress>({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: undefined,
    postalCode: "",
    country: CountryCode.US,
  });
  const [note, setNote] = useState<string>();
  const [date, setDate] = useState<string>();
  const [time, setTime] = useState<Dayjs | undefined>(undefined);
  const [timezone, setTimezone] = useState<Timezone>("America/Los_Angeles");
  const [errors, setErrors] = useState<{
    note?: string;
    streetAddress1?: string;
    city?: string;
    state?: string;
    date?: string;
    time?: string;
    timezone?: string;
    postalCode?: string;
  }>({});
  const validUser =
    isChurchManager(attributes, churchId) || isAdmin(attributes);

  useEffect(() => {
    getChurch({ churchId }).then(
      (church) => {
        setLocation(church.address!);
      },
      (fail) => {
        navigate(`/churches/${churchId}`, { replace: true });
        console.log(`Church not found, ${fail}`);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};
    if (!location.streetAddress1?.trim())
      newErrors.streetAddress1 = "Address is required.";
    if (!location.city?.trim()) newErrors.city = "City is required.";
    if (!location.state?.trim()) newErrors.state = "State is required.";
    if (!timezone?.trim()) newErrors.timezone = "Timezone is required.";
    if (!location.postalCode?.trim())
      newErrors.postalCode = "Zip Code is required.";
    if (!note?.trim()) newErrors.note = "Note is required.";
    if (!date?.trim()) newErrors.date = "Date is required.";
    if (!time) newErrors.time = "Time is required.";
    if (!timezone?.trim()) newErrors.timezone = "Time Zone is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddBaptism = async (input: CreateBaptismInput) => {
    if (!validateInputs()) return;

    createBaptism(input).then(
      (res) => {
        setAlert({
          showAlert: true,
          alertMsg: "New baptism event created",
          alertType: "success",
        });
        navigate(`/churches/${churchId}`, { replace: true });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error creating baptism event",
          alertType: "error",
        });
      },
    );
  };

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.titleBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar
          showBackArrow={true}
          handleNav={() => navigate(`/churches/${churchId}`)}
        />
      </Box>
      <Box sx={styles.innerBox}>
        <Typography
          align={"left"}
          variant={"h5"}
          fontFamily={"AmericanGrotesk-Bold"}
        >
          {"Add Baptism Event for Church"}
        </Typography>
      </Box>
      <Box sx={[styles.infoBox, { mb: 1.5 }]}>
        <Typography
          align={"left"}
          sx={{ display: "flex", flexDirection: "row" }}
        >
          <InfoIcon sx={styles.infoIcon} />
          Baptism Events can be at your church or a local body of water.
        </Typography>
      </Box>
      <Divider orientation="horizontal" />
      <Stack
        direction="column"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Street Address"}
          value={location?.streetAddress1}
          error={!!errors.streetAddress1}
          helperText={errors.streetAddress1}
          onChange={(e) =>
            setLocation({ ...location, streetAddress1: e.target.value })
          }
          sx={styles.itemContainer}
        />
        <TextField
          required={false}
          id={"outlined-required"}
          label={"Unit/Suite/Other"}
          value={location?.streetAddress2}
          onChange={(e) =>
            setLocation({ ...location, streetAddress2: e.target.value })
          }
          sx={styles.itemContainer}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", display: "flex" }}
      >
        <TextField
          required={true}
          id={"outlined-required"}
          label={"City"}
          value={location?.city}
          error={!!errors.city}
          helperText={errors.city}
          onChange={(e) => setLocation({ ...location, city: e.target.value })}
          sx={[styles.itemContainer, { flex: 0.5 }]}
        />
        <TextField
          required={true}
          select
          id={"state-select"}
          label={"State"}
          value={location?.state ?? ""}
          error={!!errors.state}
          helperText={errors.state}
          onChange={(e) =>
            setLocation({ ...location, state: e.target.value as State })
          }
          sx={[styles.itemContainer, { ml: 1, mr: 1, flex: 0.25 }]}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            select: {
              displayEmpty: true,
              native: true,
            },
          }}
        >
          <option value="" disabled>
            Select Sate
          </option>
          {Object.keys(State).map((option, index) => (
            <option key={`Select_${option}_${index}`} value={option}>
              {option}
            </option>
          ))}
        </TextField>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Zip Code"}
          value={location?.postalCode}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          onChange={(e) =>
            setLocation({ ...location, postalCode: e.target.value })
          }
          sx={[styles.itemContainer, { flex: 0.25 }]}
        />
      </Stack>
      <Grid
        container
        direction="row"
        spacing={0} // Adjust the spacing between Grid items
        justifyContent="space-between"
        alignItems="stretch"
      >
        <Grid size={4} component="div" display={"flex"}>
          <TextField
            required={true}
            select
            disabled={!validUser}
            id={"select-date"}
            label={"Select Date"}
            value={date ?? ""}
            error={!!errors.date}
            helperText={errors.date}
            onChange={(e) => setDate(e.target.value)}
            sx={{ mr: 1, width: "100%" }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              select: {
                displayEmpty: true,
                native: true,
              },
            }}
          >
            <option value="" disabled>
              Select Date
            </option>
            <option key={`Select_${"2025-06-07"}`} value={"2025-06-07"}>
              {"Saturday (6/7/2025)"}
            </option>
            <option key={`Select_${"2025-06-08"}`} value={"2025-06-08"}>
              {"Sunday (6/8/2025)"}
            </option>
          </TextField>
        </Grid>
        <Grid size={4} component="div" display={"flex"}>
          <TextField
            required={true}
            select
            disabled={!validUser}
            id={"tz-select"}
            label={"TimeZone"}
            value={timezone ?? ""}
            error={!!errors.timezone}
            helperText={errors.timezone}
            onChange={(e) => {
              setTimezone(e.target.value as Timezone);
              setTime(undefined); // force user to click time again
            }}
            sx={{ mr: 1, width: "100%" }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              select: {
                displayEmpty: true,
                native: true,
              },
            }}
          >
            <option value="" disabled>
              Select TimeZone
            </option>
            {Object.keys(Timezone)
              .filter((it) => it !== "UTC")
              .map((option, index) => (
                <option
                  key={`Select_${option}_${index}`}
                  value={Timezone[option as keyof typeof Timezone]}
                >
                  {option}
                </option>
              ))}
          </TextField>
        </Grid>
        <Grid size={4} component="div" display={"flex"}>
          <TimePicker
            sx={{ width: "100%" }}
            timezone={timezone ?? "system"}
            label={"Service Time"}
            value={time ?? null}
            handleChange={(value: Dayjs) => {
              if (!value) {
                setErrors({ ...errors, time: "Time is required." });
              } else {
                setErrors({ ...errors, time: "" });
                setTime(value);
              }
            }}
            disabled={!validUser}
            timeError={errors.time}
          />
        </Grid>
      </Grid>
      <TextField
        required={true}
        multiline
        rows={6}
        value={note}
        label={"Notes & Additional Info"}
        error={!!errors.note}
        helperText={errors.note}
        onChange={(e) => setNote(e.target.value)}
        sx={[styles.itemContainer, { mt: 3 }]}
      />
      <Box sx={styles.saveButtonBox}>
        <DebouncedButton
          variant="contained"
          sx={styles.saveButton}
          endIcon={<SaveIcon />}
          onClick={() => {
            if (!validateInputs()) return;
            handleAddBaptism({
              churchId,
              time: dateFromDateTimeTzStr(
                date ?? "",
                time?.format("HH:mm:ss") ?? "",
                timezone ?? "",
              ),
              timezone: timezone,
              address: location,
              note,
            });
          }}
        >
          {"Save"}
        </DebouncedButton>
      </Box>
    </Container>
  );
};

export default CreateBaptismEvent;
